import { useQuery } from '@tanstack/react-query';
import { CommonApiClient } from '@@/api/api-client';
import { getRolesPermissionsResponse } from '@@/api/api-common';
import { ResultValue } from '@@/types/ApiResult';
import { fetchUserAttributes } from 'aws-amplify/auth';
import { GET_ROLE_PERMISSIONS_QUERY_KEY } from '@@/products/portal/contexts/AuthProvider';
const USER_ATTRIBUTES_QUERY_KEY = 'userAttributes';

const fetchRolePermissions = async () => {
  try {
    const client = new CommonApiClient();
    const result = await client.common.getRolePermissions();

    if (result?.ok) {
      return result.data;
    }
    if (ResultValue.error(result)) {
      if (Array.isArray(result.message)) {
        throw new Error(result.message.join(', '));
      }
      throw new Error(result.message);
    }
    throw new Error('Error getting role permissions');
  } catch (error) {
    throw new Error('Error getting role permissions - ' + error);
  }
};

export const useGetUserRolePermissions = () => {
  const { data: userAttributes } = useQuery({
    queryKey: [USER_ATTRIBUTES_QUERY_KEY],
    queryFn: async () => {
      const result = await fetchUserAttributes();
      return {
        id: result['custom:user-id'] ?? '',
        email: result.email ?? '',
        companyName: result['custom:company-id'] ?? ''
      };
    },
    staleTime: Infinity, // User attributes don't change during session
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false
  });

  // Fetch role permissions
  const { data, isPending, error, refetch, isRefetching } = useQuery<getRolesPermissionsResponse | undefined, Error>({
    queryKey: [GET_ROLE_PERMISSIONS_QUERY_KEY],
    queryFn: fetchRolePermissions,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
    retry: false
  });

  return {
    permissions: data,
    isPending,
    error,
    refetch,
    isRefetching,
    user: userAttributes || { id: '', email: '', companyName: '' }
  };
};
